// ============================================================
// Client API typed — wrapper fetch avec auth + refresh auto
// ============================================================

import type {
  User,
  PublicUser,
  UserProfile,
  Community,
  Channel,
  ChannelCategory,
  Message,
  DmMessage,
  DmThreadSummary,
  DmCallGrant,
  DmCallState,
  DmAttestationGrant,
  DmPolicy,
  DmRequest,
  DmRequestCreated,
  DmRequestDirection,
  DmThreadRequestState,
  LinkedServerKeyView,
  Role,
  AuditLog,
  InstanceConfig,
  ServerInfo,
  PrivilegeKeyView,
  CommunityEventView,
  EventRsvpStatus,
  CentralAccountRef,
  CommunityEmoji,
  UseAccountServerDetails,
  VoicePresenceSnapshot,
  ChannelPermissionOverride,
  ChannelOverrideInput,
  MyCommunityPermissions,
  MyChannelPermissions,
  RoleCreateInput,
  RoleUpdateInput,
} from '@siverix/shared';
import { ERROR_USE_ACCOUNT_SERVER } from '@siverix/shared';

export type {
  DmMessage,
  DmThreadSummary,
  DmCallGrant,
  DmCallInfo,
  DmCallState,
  DmMessageKind,
  DmAttestationGrant,
  DmPolicy,
  DmRequest,
  DmRequestCreated,
  DmRequestDirection,
  DmRequestErrorCode,
  DmRequestStatus,
  DmRequestVia,
  DmThreadRequestState,
  LinkedServerKeyView,
  ServerInfo,
  PrivilegeKeyView,
  CommunityEventView,
  CommunityEventBroadcast,
  EventAttendee,
  EventRsvpCounts,
  EventRsvpStatus,
  CentralAccountRef,
  UseAccountServerDetails,
  VoicePresenceSnapshot,
  VoiceStateFlags,
  ChannelPermissionOverride,
  ChannelOverrideInput,
  MyCommunityPermissions,
  MyChannelPermissions,
  RoleCreateInput,
  RoleUpdateInput,
} from '@siverix/shared';
export {
  ERROR_USE_ACCOUNT_SERVER,
  MEMBERS_PAGE_MAX,
  DEFAULT_DM_POLICY,
  DM_POLICIES,
  DM_REQUEST_ERRORS,
  DM_REQUEST_PENDING_MAX_MESSAGES,
} from '@siverix/shared';

// [2.3] Événements de guilde. Dates = instants ISO 8601 AVEC fuseau
// (`Date.prototype.toISOString()`), jamais une heure locale nue.
export interface CreateEventInput {
  title: string;
  description?: string | null;
  startsAt: string;
  endsAt?: string | null;
  /** Salon VOCAL de la même communauté, ou null. */
  voiceChannelId?: string | null;
  /** 1..500, null = illimité. */
  capacity?: number | null;
}

export type UpdateEventInput = Partial<CreateEventInput>;

export interface ListEventsQuery {
  /** Borne basse (incluse) sur le début. */
  from?: string;
  /** Borne haute (incluse) sur le début. */
  to?: string;
  /** Inclure les événements terminés (30 derniers jours si `from` absent). */
  includePast?: boolean;
  /** 1..100 (défaut 100). */
  limit?: number;
}

// MEN-242 — filtres journal d'audit (recherche user/action/date/IP).
export interface AuditLogQuery {
  userId?: string;
  action?: string;
  /** Date ISO (incluse) borne basse sur createdAt. */
  from?: string;
  /** Date ISO (incluse) borne haute sur createdAt. */
  to?: string;
  ip?: string;
  limit?: number;
  cursor?: string;
  format?: 'json' | 'csv';
}

function auditLogQs(params?: AuditLogQuery): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// MEN-239 — modération / bans / invitations instance.
export interface AdminReport {
  id: string;
  reporterId: string;
  targetType: string;
  targetId: string;
  reason: string;
  status: 'pending' | 'investigating' | 'resolved' | 'rejected';
  createdAt: string;
  updatedAt: string;
  reporter?: {
    id: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
  } | null;
}

export interface AdminBan {
  userId: string;
  reason: string;
  bannedById: string;
  bannedAt: string;
  expiresAt: string | null;
  user?: {
    id: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
  } | null;
  bannedBy?: { id: string; username: string } | null;
}

export interface AdminInvite {
  code: string;
  createdById: string;
  maxUses: number | null;
  uses: number;
  expiresAt: string | null;
  createdAt: string;
  createdBy?: { id: string; username: string } | null;
}

// MEN-819 — invitations de communauté (parité discord.gg).
export interface CommunityInvite {
  code: string;
  communityId: string;
  maxUses: number | null;
  uses: number;
  expiresAt: string | null;
  createdAt: string;
  createdBy?: { id: string; username: string; displayName?: string };
}

export interface InvitePreview {
  code: string;
  community: {
    id: string;
    name: string;
    description?: string;
    iconUrl?: string;
    memberCount: number;
  };
  expiresAt: string | null;
  maxUses: number | null;
  uses: number;
}

// MEN-237 — rôles community (surface admin instance). Forme = publicRole API
// (bitmask `permissions` = String décimal BigInt-safe, calculé serveur).
export interface AdminRole {
  id: string;
  communityId: string;
  name: string;
  color: number;
  permissions: string;
  position: number;
  isManaged: boolean;
  hoist: boolean;
  mentionable: boolean;
  createdAt: string;
}

// MEN-259 — slice 2 : membres + ACL salon (surface admin instance).
export interface AdminMember {
  id: string;
  userId: string;
  joinedAt: string;
  user: {
    id: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
  } | null;
  roleIds: string[];
}

export interface AdminCommunityChannel {
  id: string;
  name: string;
  type: string;
  position: number;
}

export interface AdminChannelOverride {
  id: string;
  channelId: string;
  roleId: string | null;
  memberId: string | null;
  allow: string;
  deny: string;
}

// Système de liste d'amis (compte central Siverix, hors communauté).
// direction : incoming = il m'a demandé · outgoing = je l'ai demandé ·
// mutual = amis acceptés. `since` = ISO de la dernière transition.
export interface Friend {
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  status: 'pending' | 'accepted';
  direction: 'incoming' | 'outgoing' | 'mutual';
  since: string;
  // [2.7] Présents seulement dans GET /me/friends (amis acceptés) d'un
  // serveur à jour. mutualServers = URLs normalisées des serveurs favoris
  // communs, dans l'ordre de MA liste.
  mutualServers?: string[];
  mutualFriendCount?: number;
  // [2.9.2] GET /me/friends : statut vu par les autres au moment de la
  // lecture (« offline » sans connexion ou invisible). Absent : serveur < 2.9.2.
  presence?: string;
}

// [2.7] Réponse de POST /auth/sso/profile.
export interface SsoProfileSyncResult {
  user: {
    id: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
    email: string;
    isAdmin: boolean;
  };
  changed: boolean;
}

// MEN-266 — utilisateur bloqué (parité Discord). `since` = ISO du blocage.
export interface BlockedUser {
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  since: string;
}

// MEN-266 — ami en commun (parité Discord « N amis en commun »).
export interface MutualFriend {
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
}

// MEN-266 — serveur en commun (parité Discord « N serveurs en commun »).
export interface MutualCommunity {
  id: string;
  name: string;
  slug: string;
  iconUrl: string | null;
}

// MEN-266 — suggestion d'ami (parité Discord « People You May Know »).
// `mutualCount` = nombre d'amis en commun (≥ 1), classé décroissant.
export interface FriendSuggestion {
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  mutualCount: number;
}

// MEN-266 — compteurs « demandes d'amis » pour le badge (parité Discord).
// `incoming` = demandes reçues en attente ; `outgoing` = envoyées en attente.
export interface FriendRequestCounts {
  incoming: number;
  outgoing: number;
}

// MEN-266 — état de relation pour la fiche profil (parité Discord). Un
// blocage SUBI est masqué côté serveur en 'none' (jamais révélé).
export type RelationshipStatus = 'self' | 'none' | 'friends' | 'incoming' | 'outgoing' | 'blocked';

export interface Relationship {
  user: {
    id: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
  };
  status: RelationshipStatus;
  canDm: boolean;
  since: string | null;
}

/**
 * [2.2] Magasin du jeton de rafraîchissement d'un client NATIF, par serveur
 * (clé = URL du serveur). Le cookie SameSite=Strict n'est pas envoyé depuis un
 * renderer `file://` : le desktop garde le jeton dans le coffre du système.
 */
export interface RefreshTokenStore {
  get(baseUrl: string): string | null | Promise<string | null>;
  set(baseUrl: string, token: string | null): void | Promise<void>;
}

export interface ApiClientOptions {
  baseUrl: string;
  getAccessToken?: () => string | null;
  setAccessToken?: (token: string | null) => void;
  onUnauthorized?: () => void;
  fetchImpl?: typeof fetch;
  /** [2.2] Client natif : envoie `X-Siverix-Client` (le serveur renvoie alors le jeton de rafraîchissement). */
  clientKind?: 'desktop' | 'mobile';
  refreshTokenStore?: RefreshTokenStore;
}

export interface AuthResponse {
  accessToken: string;
  expiresIn: number;
  user: User;
  /** [2.2] Présent seulement pour un client natif (cf. RefreshTokenStore). */
  refreshToken?: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

// [2.13.0] Serveur Siverix vu par la console du serveur de comptes
// (GET /admin/siverix-servers) : agrégé depuis les comptes reliés et les favoris.
export interface SiverixServerSummary {
  /** Adresse canonique du serveur Siverix. */
  url: string;
  label: string | null;
  /** Comptes Siverix reliés (clé enregistrée en s'y connectant). */
  linkedAccounts: number;
  /** Comptes Siverix qui l'ont en favori. */
  bookmarks: number;
  lastSeenAt: string | null;
  blocked: boolean;
  blockedReason: string | null;
  blockedAt: string | null;
}

// [2.13.0] Sonde d'état d'un serveur Siverix (GET /admin/siverix-servers/status).
export interface SiverixServerStatus {
  reachable: boolean;
  name: string | null;
  version: string | null;
  claimed: boolean | null;
  motd: string | null;
}

// MEN-748 — serveur communautaire (favori) du compte central. La liste vit
// côté serveur (table server_bookmarks) et suit le compte sur tous les appareils.
export interface ServerBookmark {
  id: string;
  url: string;
  label: string | null;
  position: number;
  createdAt: string;
  updatedAt: string;
}

// [P01] Vue read-state d'un salon (badges non-lus).
export interface ChannelReadStateView {
  channelId: string;
  unreadCount: number;
  lastReadAt: string | null;
  lastReadMessageId: string | null;
}

// [P22] Webhook entrant (token inclus : à copier dans le service externe).
export interface WebhookView {
  id: string;
  name: string;
  channelId: string;
  token: string;
  url: string;
  createdAt: string;
}

// [P20] Emoji personnalisé de communauté. [2.10.0] Même forme que l'événement
// emoji:created (type partagé).
export type CommunityEmojiView = CommunityEmoji;

// [2.10.0] Ligne de GET /communities/:id/members. `roleIds` et `account` :
// présents à partir de la 2.10.0 (absents sur un serveur plus ancien).
export interface CommunityMemberRow {
  /** Identifiant d'ADHÉSION (CommunityMember.id) ; avec joinedAt, curseur de pagination. */
  id: string;
  userId: string;
  joinedAt: string;
  user?: PublicUser & { status?: string | null };
  /** Rôles attribués au membre (sans @everyone). */
  roleIds?: string[];
  /** Compte Siverix central du membre ; null = compte de ce serveur. */
  account?: CentralAccountRef | null;
}

// [2.10.0] Ligne de GET /communities/:cid/members/search (`id` = id UTILISATEUR).
export interface MemberSearchResult extends PublicUser {
  roleIds?: string[];
  account?: CentralAccountRef | null;
  /**
   * [2.12] Identifiant d'ADHÉSION (CommunityMember.id) : à passer comme
   * `memberId` d'une surcharge de salon. Absent d'un serveur plus ancien.
   */
  memberId?: string;
}

/**
 * Verdict de GET /me/dms/:userId/can-send. [2.10.0] 'blocked' : seulement pour
 * celui qui A BLOQUÉ ; la personne bloquée lit 'not-friends' (un blocage subi
 * n'est jamais révélé).
 * [2.11.0] Demandes de message :
 *  - 'request-sent'     : ma demande est en attente ; `canSend` tant qu'il me
 *    reste des messages (`request.remaining`), texte seulement ;
 *  - 'request-received' : il m'a envoyé une demande en attente ; je l'accepte
 *    avant d'écrire (`canSend` faux) ;
 *  - 'request-closed'   : ma demande a été refusée, annulée ou bloquée.
 * 'ok' couvre aussi une demande acceptée (MP normal).
 */
export type DmSendReason =
  | 'self'
  | 'not-friends'
  | 'blocked'
  | 'ok'
  | 'request-sent'
  | 'request-received'
  | 'request-closed';

/** [2.11.0] Corps de POST /me/dm-requests : chemin fédéré (attestation) ou local (id). */
export type SendDmRequestInput =
  /** Fédéré : attestation d'un serveur de guilde (POST /me/dm-attestations là-bas). */
  | { attestation: string; content: string }
  /** Local : destinataire de CE serveur, avec qui je partage une communauté. */
  | { userId: string; content: string };

/** [2.11.0] Corps de POST /me/linked-server-keys. */
export interface RegisterLinkedServerKeyInput {
  /** URL du serveur de guilde (normalisée par le serveur de comptes). */
  audience: string;
  kid: string;
  /** Clé publique PEM (SPKI) lue sur GET /auth/sso/pubkey de la guilde. */
  publicKey: string;
  /** Nom du serveur tel que je le vois (« via <nom> »), 80 caractères au plus. */
  label?: string | null;
}

/** [2.11.0] Réponse de GET /auth/sso/pubkey (clé publique d'un serveur). */
export interface ServerPublicKey {
  kid: string;
  alg: 'RS256';
  issuer: string;
  publicKey: string;
}

/**
 * [2.11.0] Identifiant de demande porté par une erreur 409 RequestPending ou
 * IncomingRequestPending (`details.requestId`), sinon null.
 */
export function dmRequestIdOf(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null;
  const d = e.details as { requestId?: unknown } | undefined;
  return typeof d?.requestId === 'string' && d.requestId !== '' ? d.requestId : null;
}

/**
 * [2.10.0] Vrai si l'erreur dit « fais-le sur le serveur de comptes » (409
 * UseAccountServer d'un serveur de guilde) ; `details.accountServer` donne son
 * adresse.
 */
export function isUseAccountServerError(
  e: unknown,
): e is ApiError & { details: UseAccountServerDetails } {
  if (!(e instanceof ApiError) || e.code !== ERROR_USE_ACCOUNT_SERVER) return false;
  const d = e.details as { accountServer?: unknown } | undefined;
  return typeof d?.accountServer === 'string' && d.accountServer !== '';
}

export class SiverixApi {
  private baseUrl: string;
  private getToken: () => string | null;
  private setToken: (t: string | null) => void;
  private onUnauthorized?: () => void;
  private fetchImpl: typeof fetch;
  private refreshing: Promise<string | null> | null = null;
  private clientKind?: 'desktop' | 'mobile';
  private refreshStore?: RefreshTokenStore;

  constructor(opts: ApiClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, '');
    this.getToken = opts.getAccessToken ?? (() => null);
    this.setToken = opts.setAccessToken ?? (() => undefined);
    this.onUnauthorized = opts.onUnauthorized;
    this.clientKind = opts.clientKind;
    this.refreshStore = opts.refreshTokenStore;
    // MEN-251 — `globalThis.fetch` DOIT etre invoque avec `this` === window/
    // globalThis. Stocke brut puis appele via `this.fetchImpl(...)` => `this`
    // devient l'instance SiverixApi => TypeError "Failed to execute 'fetch'
    // on 'Window': Illegal invocation" dans tout navigateur (admin web +
    // renderer Electron). Wrapper lazy bound : resilient si fetch est
    // polyfille apres construction, et ne touche pas un fetchImpl injecte.
    this.fetchImpl =
      opts.fetchImpl ??
      ((...args: Parameters<typeof fetch>): ReturnType<typeof fetch> => globalThis.fetch(...args));
  }

  /** MEN-261 — repointe le client vers un autre serveur Siverix (rail multi-serveurs). */
  setBaseUrl(url: string): void {
    this.baseUrl = url.replace(/\/$/, '');
  }

  /** [2.2] URL du serveur actuellement visé (sans slash final). */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * [2.2] Résout une URL de média renvoyée par l'API. Les fichiers servis par
   * le serveur (pièces jointes, avatars, emojis) sont stockés en chemin RELATIF
   * (`/files/<id>/<nom>`) pour survivre à un changement d'adresse du serveur :
   * on les rattache au serveur visé. Les URLs absolues et data:/blob: passent
   * telles quelles.
   */
  resolveUrl(url: string | null | undefined): string {
    if (url === null || url === undefined || url === '') return '';
    if (url.startsWith('/') && !url.startsWith('//')) return `${this.baseUrl}${url}`;
    return url;
  }

  private async request<T>(method: string, path: string, body?: unknown, retry = true): Promise<T> {
    const headers: Record<string, string> = this.clientHeaders();
    const token = this.getToken();
    if (token) headers.authorization = `Bearer ${token}`;
    if (body !== undefined) headers['content-type'] = 'application/json';

    const res = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method,
      headers,
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (res.status === 401 && retry && path !== '/auth/refresh') {
      const newToken = await this.refresh();
      if (newToken) return this.request<T>(method, path, body, false);
      this.onUnauthorized?.();
    }

    if (!res.ok) {
      let payload: { error?: string; message?: string; details?: unknown } = {};
      try {
        payload = await res.json();
      } catch {
        /* */
      }
      throw new ApiError(
        res.status,
        payload.error ?? 'HttpError',
        payload.message ?? res.statusText,
        payload.details,
      );
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  private clientHeaders(): Record<string, string> {
    return this.clientKind ? { 'x-siverix-client': this.clientKind } : {};
  }

  /** [2.2] Mémorise le jeton de rafraîchissement renvoyé à un client natif. */
  private async keepRefreshToken(r: { refreshToken?: string }): Promise<void> {
    if (this.refreshStore && typeof r.refreshToken === 'string') {
      await this.refreshStore.set(this.baseUrl, r.refreshToken);
    }
  }

  /**
   * [2.10.0] Rafraîchissement de session en cours (null : aucun). Le serveur
   * remplace le jeton de rafraîchissement dès la requête : recharger la page
   * avant la fin perdrait le nouveau (et réutiliser l'ancien ferme la session).
   */
  refreshInFlight(): Promise<unknown> | null {
    return this.refreshing;
  }

  private async refresh(): Promise<string | null> {
    if (this.refreshing) return this.refreshing;
    this.refreshing = (async () => {
      try {
        const stored = this.refreshStore ? await this.refreshStore.get(this.baseUrl) : null;
        const res = await this.fetchImpl(`${this.baseUrl}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: stored
            ? { ...this.clientHeaders(), 'content-type': 'application/json' }
            : this.clientHeaders(),
          body: stored ? JSON.stringify({ refreshToken: stored }) : undefined,
        });
        if (!res.ok) return null;
        const payload = (await res.json()) as AuthResponse;
        this.setToken(payload.accessToken);
        await this.keepRefreshToken(payload);
        return payload.accessToken;
      } catch {
        return null;
      } finally {
        this.refreshing = null;
      }
    })();
    return this.refreshing;
  }

  // ----- Auth -----
  signup(data: {
    email: string;
    username: string;
    password: string;
    displayName?: string;
    inviteCode?: string;
  }) {
    return this.request<AuthResponse>('POST', '/auth/signup', data).then(async (r) => {
      this.setToken(r.accessToken);
      await this.keepRefreshToken(r);
      return r;
    });
  }
  login(data: { emailOrUsername: string; password: string }) {
    return this.request<AuthResponse>('POST', '/auth/login', data).then(async (r) => {
      this.setToken(r.accessToken);
      await this.keepRefreshToken(r);
      return r;
    });
  }
  async logout() {
    const stored = this.refreshStore ? await this.refreshStore.get(this.baseUrl) : null;
    try {
      return await this.request<{ ok: true }>(
        'POST',
        '/auth/logout',
        stored ? { refreshToken: stored } : undefined,
      );
    } finally {
      // Même si le serveur est injoignable : la session locale est oubliée.
      this.setToken(null);
      if (this.refreshStore) await this.refreshStore.set(this.baseUrl, null);
    }
  }
  // MEN-823 — change le mot de passe. Le serveur révoque les sessions des
  // autres appareils et ré-émet des tokens frais pour celui-ci.
  changePassword(currentPassword: string, newPassword: string) {
    return this.request<{
      changed: true;
      accessToken: string;
      expiresIn: number;
      refreshToken?: string;
    }>('POST', '/auth/change-password', { currentPassword, newPassword }).then(async (r) => {
      this.setToken(r.accessToken);
      await this.keepRefreshToken(r);
      return r;
    });
  }
  me() {
    return this.request<{ user: User }>('GET', '/auth/me');
  }

  // ----- SSO fédéré (MEN-262) -----
  // Rôle IdP : demande au serveur courant (le compte central Siverix sur
  // lequel on est authentifié) une assertion RS256 signée, à courte durée
  // de vie, destinée au serveur `audience` (le serveur auto-hébergé du pote).
  ssoAssertion(audience: string) {
    return this.request<{ assertion: string; issuer: string }>('POST', '/auth/sso/assertion', {
      audience,
    });
  }
  // Rôle SP : présente au serveur courant (le serveur du pote) l'assertion
  // émise par l'IdP. Le serveur la vérifie (RS256, pubkey de l'IdP),
  // lie/crée le compte local et ouvre une session locale (cookie refresh +
  // accessToken) — comme login(), sans mot de passe.
  ssoLogin(data: { assertion: string; issuer: string }) {
    return this.request<AuthResponse>('POST', '/auth/sso/login', data).then(async (r) => {
      this.setToken(r.accessToken);
      await this.keepRefreshToken(r);
      return r;
    });
  }

  // [2.7] Rôle SP : recopie en miroir le pseudo et l'avatar du compte sur
  // l'utilisateur local lié, avec une assertion fraîche (sans nouvelle session).
  // Un serveur plus ancien répond 404 : l'appelant l'ignore.
  syncSsoProfile(data: { issuer: string; assertion: string }) {
    return this.request<SsoProfileSyncResult>('POST', '/auth/sso/profile', data);
  }

  // [2.11.0] Clé publique de CE serveur (public). Le bureau la lit sur une
  // guilde puis l'enregistre sur le compte (registerLinkedServerKey).
  ssoPubkey() {
    return this.request<ServerPublicKey>('GET', '/auth/sso/pubkey');
  }

  // ----- Users -----
  updateMe(data: {
    displayName?: string | null;
    avatarUrl?: string | null;
    bio?: string | null;
    status?: 'online' | 'idle' | 'dnd' | 'invisible';
    /** [2.11.0] Qui peut m'envoyer une demande de message. */
    dmPolicy?: DmPolicy;
  }) {
    return this.request<{ user: User }>('PATCH', '/users/me', data);
  }
  // [P15] profil public d'un utilisateur (carte de profil, parité Discord).
  getUserProfile(id: string) {
    return this.request<UserProfile>('GET', `/users/${id}`);
  }
  deleteMe() {
    return this.request<{ deleted: true }>('DELETE', '/users/me');
  }

  // ----- Communities -----
  listMyCommunities() {
    return this.request<Community[]>('GET', '/communities');
  }
  createCommunity(data: { name: string; slug: string; description?: string; isPublic?: boolean }) {
    return this.request<Community>('POST', '/communities', data);
  }
  getCommunity(id: string) {
    return this.request<Community>('GET', `/communities/${id}`);
  }
  // MEN-821 — gestion du serveur (paramètres + suppression + expulsion).
  updateCommunity(
    id: string,
    data: {
      name?: string;
      description?: string | null;
      iconUrl?: string | null;
      isPublic?: boolean;
    },
  ) {
    return this.request<Community>('PATCH', `/communities/${id}`, data);
  }
  deleteCommunity(id: string) {
    return this.request<void>('DELETE', `/communities/${id}`);
  }
  kickMember(communityId: string, userId: string) {
    return this.request<void>('DELETE', `/communities/${communityId}/members/${userId}`);
  }
  joinCommunity(id: string) {
    return this.request<{ joined: true }>('POST', `/communities/${id}/join`);
  }
  leaveCommunity(id: string) {
    return this.request<{ left: true }>('POST', `/communities/${id}/leave`);
  }
  // MEN-819 — invitations de communauté (parité discord.gg).
  createCommunityInvite(
    communityId: string,
    data?: { maxUses?: number | null; expiresInHours?: number | null },
  ) {
    return this.request<CommunityInvite>('POST', `/communities/${communityId}/invites`, data ?? {});
  }
  listCommunityInvites(communityId: string) {
    return this.request<CommunityInvite[]>('GET', `/communities/${communityId}/invites`);
  }
  revokeCommunityInvite(communityId: string, code: string) {
    return this.request<void>('DELETE', `/communities/${communityId}/invites/${code}`);
  }
  getInvitePreview(code: string) {
    // /invites/:code = préview des invitations d'INSTANCE (signup) — les
    // invitations de communauté vivent sous /community-invites/:code.
    return this.request<InvitePreview>('GET', `/community-invites/${encodeURIComponent(code)}`);
  }
  joinByInviteCode(code: string) {
    return this.request<{ joined: true; alreadyMember?: boolean; community: Community }>(
      'POST',
      `/community-invites/${encodeURIComponent(code)}/join`,
    );
  }
  // [2.10.0] Pagination : `after` = DERNIÈRE ligne de la page précédente (son
  // arrivée et son id d'adhésion, envoyés en afterJoinedAt + afterId : rien à
  // relire côté serveur, même si ce membre est parti entre-temps) ; `limit`
  // 1..MEMBERS_PAGE_MAX (défaut serveur : MEMBERS_PAGE_MAX). Ordre : arrivée,
  // puis id. Une page plus courte que `limit` est la dernière. Un serveur plus
  // ancien ignore ces paramètres (il renvoie toujours la première page).
  // [2.12] `roleId` : seulement les membres qui ont ce rôle (onglet « Membres »
  // de l'éditeur de rôle) ; un serveur plus ancien l'ignore.
  listMembers(
    communityId: string,
    opts?: {
      after?: Pick<CommunityMemberRow, 'id' | 'joinedAt'>;
      limit?: number;
      roleId?: string;
    },
  ) {
    const params = new URLSearchParams();
    if (opts?.after) {
      params.set('afterJoinedAt', opts.after.joinedAt);
      params.set('afterId', opts.after.id);
    }
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.roleId) params.set('roleId', opts.roleId);
    const qs = params.toString();
    return this.request<CommunityMemberRow[]>(
      'GET',
      `/communities/${communityId}/members${qs ? `?${qs}` : ''}`,
    );
  }
  // [P05/P06] recherche de membres pour l'autocomplete des @mentions.
  searchCommunityMembers(communityId: string, q: string, limit = 10) {
    const params = new URLSearchParams({ q, limit: String(limit) });
    return this.request<MemberSearchResult[]>(
      'GET',
      `/communities/${communityId}/members/search?${params}`,
    );
  }
  // [P17] permissions effectives du membre courant (pour gater l'UI).
  // [2.12] `hierarchy` : ma place dans la hiérarchie des rôles.
  getMyPermissions(communityId: string) {
    return this.request<MyCommunityPermissions>(
      'GET',
      `/communities/${communityId}/me/permissions`,
    );
  }
  // Rôles de la communauté (lecture ouverte à tout membre) : couleurs, ordre,
  // regroupement dans la liste des membres.
  listRoles(communityId: string) {
    return this.request<Role[]>('GET', `/communities/${communityId}/roles`);
  }

  // ----- [2.12] Rôles et permissions d'un serveur (MANAGE_ROLES) -----
  // Refus : ApiError.code 'RoleHierarchy' (rôle ou membre au niveau du mien ou
  // au-dessus), 'PermissionEscalation' (permission que je n'ai pas),
  // 'ManagedRole' (@everyone) ; ApiError.message est en français.

  /** Nouveau rôle, placé juste au-dessus de @everyone. */
  createRole(communityId: string, data: RoleCreateInput) {
    return this.request<Role>('POST', `/communities/${communityId}/roles`, data);
  }
  /** @everyone : seules les permissions comptent. */
  updateRole(roleId: string, data: RoleUpdateInput) {
    return this.request<Role>('PATCH', `/roles/${roleId}`, data);
  }
  deleteRole(roleId: string) {
    return this.request<void>('DELETE', `/roles/${roleId}`);
  }
  /** `order` : ids du plus haut au plus bas, sans @everyone. Renvoie tous les rôles. */
  reorderRoles(communityId: string, order: string[]) {
    return this.request<Role[]>('PUT', `/communities/${communityId}/roles/positions`, { order });
  }
  /** `userId` : id UTILISATEUR du membre. */
  assignRole(communityId: string, userId: string, roleId: string) {
    return this.request<{ assigned: true }>(
      'PUT',
      `/communities/${communityId}/members/${userId}/roles/${roleId}`,
      {},
    );
  }
  revokeRole(communityId: string, userId: string, roleId: string) {
    return this.request<{ removed: true }>(
      'DELETE',
      `/communities/${communityId}/members/${userId}/roles/${roleId}`,
    );
  }
  /** Surcharges d'un salon (MANAGE_ROLES). */
  listChannelOverrides(channelId: string) {
    return this.request<ChannelPermissionOverride[]>('GET', `/channels/${channelId}/permissions`);
  }
  /** Pose ou remplace la surcharge d'un rôle (`roleId`) ou d'un membre (`memberId` = id d'adhésion). */
  putChannelOverride(channelId: string, data: ChannelOverrideInput) {
    return this.request<ChannelPermissionOverride>(
      'PUT',
      `/channels/${channelId}/permissions`,
      data,
    );
  }
  deleteChannelOverride(channelId: string, overrideId: string) {
    return this.request<void>('DELETE', `/channels/${channelId}/permissions/${overrideId}`);
  }
  /** Mes droits dans un salon (surcharges comprises). */
  getMyChannelPermissions(channelId: string) {
    return this.request<MyChannelPermissions>('GET', `/channels/${channelId}/permissions/me`);
  }

  // ----- Servers (multi-serveur, data côté serveur — MEN-748) -----
  // À appeler pointé sur le serveur Accueil (compte central) où vit la liste.
  listServers() {
    return this.request<{ servers: ServerBookmark[] }>('GET', '/me/servers');
  }
  addServerBookmark(data: { url: string; label?: string }) {
    return this.request<ServerBookmark>('POST', '/me/servers', data);
  }
  updateServerBookmark(id: string, data: { label?: string | null; position?: number }) {
    return this.request<ServerBookmark>('PATCH', `/me/servers/${id}`, data);
  }
  removeServerBookmark(id: string) {
    return this.request<{ ok: true }>('DELETE', `/me/servers/${id}`);
  }

  // ----- Read-states (non-lus par salon — P01) -----
  markChannelRead(channelId: string, messageId?: string) {
    return this.request<{
      channelId: string;
      lastReadAt: string;
      lastReadMessageId: string | null;
      unreadCount: number;
    }>('POST', `/channels/${channelId}/read`, messageId ? { messageId } : {});
  }
  getCommunityReadStates(communityId: string) {
    return this.request<{ readStates: ChannelReadStateView[] }>(
      'GET',
      `/communities/${communityId}/read-states`,
    );
  }

  // ----- Réactions messages de salon (P03) -----
  toggleMessageReaction(messageId: string, emoji: string) {
    return this.request<{
      messageId: string;
      action: 'added' | 'removed';
      reactions: { emoji: string; count: number; userIds: string[] }[];
    }>('POST', `/messages/${messageId}/reactions`, { emoji });
  }

  // ----- Channels -----
  listChannels(communityId: string) {
    return this.request<Channel[]>('GET', `/communities/${communityId}/channels`);
  }
  createChannel(
    communityId: string,
    data: {
      name: string;
      type?: 'TEXT' | 'VOICE';
      topic?: string;
      slowmodeSeconds?: number;
      userLimit?: number | null;
      categoryId?: string | null;
    },
  ) {
    return this.request<Channel>('POST', `/communities/${communityId}/channels`, data);
  }
  updateChannel(
    id: string,
    data: {
      name?: string;
      topic?: string | null;
      position?: number;
      slowmodeSeconds?: number;
      userLimit?: number | null;
      categoryId?: string | null;
    },
  ) {
    return this.request<Channel>('PATCH', `/channels/${id}`, data);
  }
  deleteChannel(id: string) {
    return this.request<void>('DELETE', `/channels/${id}`);
  }
  // Catégories de salons (groupes, parité Discord).
  listCategories(communityId: string) {
    return this.request<ChannelCategory[]>('GET', `/communities/${communityId}/categories`);
  }
  createCategory(communityId: string, name: string) {
    return this.request<ChannelCategory>('POST', `/communities/${communityId}/categories`, {
      name,
    });
  }
  updateCategory(id: string, data: { name?: string; position?: number }) {
    return this.request<ChannelCategory>('PATCH', `/categories/${id}`, data);
  }
  deleteCategory(id: string) {
    return this.request<void>('DELETE', `/categories/${id}`);
  }
  // [P22] webhooks entrants (gestion ; l'exécution se fait en HTTP brut par le service externe).
  listWebhooks(channelId: string) {
    return this.request<WebhookView[]>('GET', `/channels/${channelId}/webhooks`);
  }
  createWebhook(channelId: string, name: string) {
    return this.request<WebhookView>('POST', `/channels/${channelId}/webhooks`, { name });
  }
  deleteWebhook(id: string) {
    return this.request<void>('DELETE', `/webhooks/${id}`);
  }
  // [2.3] Événements de guilde (raids, donjons, sorties) + inscriptions.
  listEvents(communityId: string, query?: ListEventsQuery) {
    const sp = new URLSearchParams();
    if (query?.from) sp.set('from', query.from);
    if (query?.to) sp.set('to', query.to);
    if (query?.includePast) sp.set('includePast', 'true');
    if (query?.limit) sp.set('limit', String(query.limit));
    const qs = sp.toString();
    return this.request<{ events: CommunityEventView[]; hasMore: boolean }>(
      'GET',
      `/communities/${communityId}/events${qs ? `?${qs}` : ''}`,
    );
  }
  createEvent(communityId: string, data: CreateEventInput) {
    return this.request<CommunityEventView>('POST', `/communities/${communityId}/events`, data);
  }
  updateEvent(id: string, data: UpdateEventInput) {
    return this.request<CommunityEventView>('PATCH', `/events/${id}`, data);
  }
  /** Annulation douce (l'événement reste visible, marqué annulé). */
  cancelEvent(id: string) {
    return this.request<void>('DELETE', `/events/${id}`);
  }
  /** `note` absente = note conservée ; null = effacée. */
  rsvpEvent(id: string, data: { status: EventRsvpStatus; note?: string | null }) {
    return this.request<CommunityEventView>('PUT', `/events/${id}/rsvp`, data);
  }
  clearRsvp(id: string) {
    return this.request<CommunityEventView>('DELETE', `/events/${id}/rsvp`);
  }
  // [P20] emojis personnalisés de communauté.
  listEmojis(communityId: string) {
    return this.request<CommunityEmojiView[]>('GET', `/communities/${communityId}/emojis`);
  }
  createEmoji(communityId: string, name: string, url: string) {
    return this.request<CommunityEmojiView>('POST', `/communities/${communityId}/emojis`, {
      name,
      url,
    });
  }
  deleteEmoji(id: string) {
    return this.request<void>('DELETE', `/emojis/${id}`);
  }

  // ----- Messages -----
  listMessages(channelId: string, opts?: { before?: string; after?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (opts?.before) params.set('before', opts.before);
    if (opts?.after) params.set('after', opts.after);
    if (opts?.limit) params.set('limit', String(opts.limit));
    return this.request<Message[]>('GET', `/channels/${channelId}/messages?${params}`);
  }
  sendMessage(
    channelId: string,
    data: { content: string; attachmentIds?: string[]; replyToMessageId?: string },
  ) {
    return this.request<Message>('POST', `/channels/${channelId}/messages`, data);
  }
  editMessage(id: string, content: string) {
    return this.request<Message>('PATCH', `/messages/${id}`, { content });
  }
  deleteMessage(id: string) {
    return this.request<void>('DELETE', `/messages/${id}`);
  }
  // [P12] recherche plein-texte simple dans un salon.
  searchMessages(channelId: string, q: string, limit = 25) {
    const params = new URLSearchParams({ q, limit: String(limit) });
    return this.request<Message[]>('GET', `/channels/${channelId}/messages/search?${params}`);
  }
  // [P14] épinglage : toggle + liste des épinglés du salon.
  pinMessage(messageId: string) {
    return this.request<{ messageId: string; pinned: boolean; pinnedAt?: string }>(
      'POST',
      `/messages/${messageId}/pin`,
    );
  }
  getChannelPins(channelId: string) {
    return this.request<Message[]>('GET', `/channels/${channelId}/pins`);
  }

  // ----- Files -----
  signUpload(data: { filename: string; mimeType: string; sizeBytes: number }) {
    return this.request<{ storageKey: string; uploadUrl: string; expiresIn: number }>(
      'POST',
      '/files/sign',
      data,
    );
  }
  finalizeUpload(data: {
    storageKey: string;
    filename: string;
    mimeType: string;
    sizeBytes: number;
  }) {
    return this.request<{
      id: string;
      filename: string;
      url: string;
      mimeType: string;
      sizeBytes: number;
    }>('POST', '/files/finalize', data);
  }

  // ----- Voice -----
  // ----- [2.2] Serveur de guilde -----
  getServerInfo() {
    return this.request<ServerInfo>('GET', '/server-info');
  }
  usePrivilegeKey(key: string) {
    return this.request<{
      granted: Array<'instance_admin' | 'community_owner'>;
      communityId: string | null;
    }>('POST', '/privilege-keys/use', { key });
  }
  adminListPrivilegeKeys() {
    return this.request<{ keys: PrivilegeKeyView[] }>('GET', '/admin/privilege-keys');
  }
  adminCreatePrivilegeKey(data: { note?: string; expiresInHours?: number } = {}) {
    return this.request<{ key: string; view: PrivilegeKeyView }>(
      'POST',
      '/admin/privilege-keys',
      data,
    );
  }
  adminRevokePrivilegeKey(id: string) {
    return this.request<void>('DELETE', `/admin/privilege-keys/${id}`);
  }
  voiceMoveMember(channelId: string, userId: string, targetChannelId: string) {
    return this.request<{ ok: true }>(
      'POST',
      `/voice/channels/${channelId}/members/${userId}/move`,
      { targetChannelId },
    );
  }
  voiceMuteMember(channelId: string, userId: string, muted: boolean) {
    return this.request<{ ok: true }>(
      'POST',
      `/voice/channels/${channelId}/members/${userId}/mute`,
      { muted },
    );
  }
  voiceDisconnectMember(channelId: string, userId: string) {
    return this.request<{ ok: true }>(
      'POST',
      `/voice/channels/${channelId}/members/${userId}/disconnect`,
    );
  }

  voiceToken(channelId: string) {
    return this.request<{ url: string; token: string; room: string; identity: string }>(
      'POST',
      '/voice/token',
      { channelId },
    );
  }

  /**
   * Instantané de la présence vocale : salon -> userIds (salons vides omis).
   * [2.10.0] `states` : dernier état (micro, casque, caméra, partage) de chacun.
   */
  voicePresence(communityId: string) {
    return this.request<VoicePresenceSnapshot>('GET', `/communities/${communityId}/voice-presence`);
  }

  // ----- Push notifications -----
  registerPushToken(data: { token: string; platform: 'ios' | 'android'; deviceName?: string }) {
    return this.request<{ ok: true }>('POST', '/notifications/register', data);
  }
  unregisterPushToken(data: { token: string }) {
    return this.request<{ ok: true }>('POST', '/notifications/unregister', data);
  }

  // ----- Amis (compte central Siverix) -----
  listFriends() {
    return this.request<Friend[]>('GET', '/me/friends');
  }
  listPendingFriends() {
    return this.request<Friend[]>('GET', '/me/friends/pending');
  }
  // [2.10.0] `userId` : viser directement un compte par son identifiant (un
  // membre de guilde vu par son compte central, CentralAccountRef.id), sans
  // passer par un pseudo qui peut désigner quelqu'un d'autre. Un seul des trois.
  // 429 au-delà de la limite de demandes.
  requestFriend(data: { username?: string; email?: string; userId?: string }) {
    return this.request<Friend>('POST', '/me/friends/requests', data);
  }
  acceptFriend(userId: string) {
    return this.request<Friend>('POST', `/me/friends/${userId}/accept`);
  }
  removeFriend(userId: string) {
    return this.request<{ ok: true }>('DELETE', `/me/friends/${userId}`);
  }
  // Amis en commun avec un utilisateur (parité Discord). Read-only, additif.
  getMutualFriends(userId: string) {
    return this.request<MutualFriend[]>('GET', `/me/friends/${userId}/mutual`);
  }
  // [2.7] URLs des serveurs favoris en commun avec un ami accepté ([] sinon).
  getMutualServers(userId: string) {
    return this.request<{ urls: string[] }>('GET', `/users/${userId}/mutual-servers`);
  }
  // Serveurs en commun avec un utilisateur (parité Discord « N serveurs
  // en commun »). Read-only, additif.
  getMutualCommunities(userId: string) {
    return this.request<MutualCommunity[]>('GET', `/me/friends/${userId}/mutual-communities`);
  }
  // État de relation pour une fiche profil (parité Discord). Read-only,
  // additif. Le serveur masque un blocage subi en status 'none'.
  getRelationship(userId: string) {
    return this.request<Relationship>('GET', `/me/relationships/${userId}`);
  }
  // Suggestions d'amis « People You May Know » (parité Discord).
  // Read-only, additif. `limit` 1..50 (défaut 20 côté serveur).
  getFriendSuggestions(limit?: number) {
    const qs = typeof limit === 'number' ? `?limit=${encodeURIComponent(limit)}` : '';
    return this.request<FriendSuggestion[]>('GET', `/me/friend-suggestions${qs}`);
  }
  // Compteurs « demandes d'amis » (badge, parité Discord). Endpoint léger
  // de polling : reçues/envoyées en attente. Read-only, additif.
  getFriendRequestCounts() {
    return this.request<FriendRequestCounts>('GET', '/me/friends/requests/count');
  }
  // Recherche dans la liste d'amis (barre de recherche, parité Discord).
  // Filtre/classe mes amis acceptés par pertinence (exact > préfixe >
  // sous-chaîne, username + displayName, insensible casse). `q` vide/
  // absent => liste complète triée alpha. Read-only, additif. Forme de
  // sortie identique à MutualFriend ({id,username,displayName,avatarUrl}).
  searchFriends(q?: string) {
    const qs = q ? `?q=${encodeURIComponent(q)}` : '';
    return this.request<MutualFriend[]>('GET', `/me/friends/search${qs}`);
  }
  // ----- Blocage utilisateur (MEN-266, parité Discord) -----
  blockUser(userId: string) {
    return this.request<{ ok: true; blocked: true }>('POST', '/me/blocks', {
      userId,
    });
  }
  unblockUser(userId: string) {
    return this.request<{ ok: true }>('DELETE', `/me/blocks/${userId}`);
  }
  listBlocked() {
    return this.request<BlockedUser[]>('GET', '/me/blocks');
  }

  // ----- Messagerie directe entre amis (MEN-263) -----
  listDms() {
    return this.request<DmThreadSummary[]>('GET', '/me/dms');
  }
  dmHistory(userId: string, opts?: { limit?: number; before?: string }) {
    const p = new URLSearchParams();
    if (opts?.limit) p.set('limit', String(opts.limit));
    if (opts?.before) p.set('before', opts.before);
    const qs = p.toString();
    return this.request<{ threadId: string; peer: PublicUser | null; messages: DmMessage[] }>(
      'GET',
      `/me/dms/${userId}/messages${qs ? `?${qs}` : ''}`,
    );
  }
  // MEN-266 (parité Discord) — recherche substring case-insensitive dans
  // une conversation. Résultats du plus récent au plus ancien ;
  // `nextBefore` (createdAt du dernier) à repasser en `before` pour la
  // page suivante (null = fin). Read-only, friends-gated, additif.
  searchDmMessages(userId: string, q: string, opts?: { limit?: number; before?: string }) {
    const p = new URLSearchParams();
    p.set('q', q);
    if (opts?.limit) p.set('limit', String(opts.limit));
    if (opts?.before) p.set('before', opts.before);
    return this.request<{
      threadId: string;
      peer: PublicUser | null;
      query: string;
      messages: DmMessage[];
      nextBefore: string | null;
    }>('GET', `/me/dms/${userId}/messages/search?${p.toString()}`);
  }
  // Recherche globale DM (MEN-266, parité Discord « Search in Direct
  // Messages ») : balaie toutes mes conversations en une requête.
  // Chaque résultat est un message annoté du `peer` (interlocuteur du
  // thread). Curseur `nextBefore` opaque (lookahead). Lecture seule.
  searchAllDms(q: string, opts?: { limit?: number; before?: string }) {
    const p = new URLSearchParams();
    p.set('q', q);
    if (opts?.limit) p.set('limit', String(opts.limit));
    if (opts?.before) p.set('before', opts.before);
    return this.request<{
      query: string;
      results: Array<DmMessage & { peer: PublicUser | null }>;
      nextBefore: string | null;
    }>('GET', `/me/dms/search?${p.toString()}`);
  }
  // Saut vers un message (parité Discord « jump to message ») — contexte autour d'un
  // message : la cible + `before` voisins plus anciens + `after` voisins
  // plus récents (ordre chronologique), avec hasMoreBefore/hasMoreAfter
  // (lookahead). Sert au clic sur un résultat de recherche pour sauter
  // au message même hors scrollback courant. Read-only, friends-gated.
  getDmMessageContext(
    userId: string,
    messageId: string,
    opts?: { before?: number; after?: number },
  ) {
    const p = new URLSearchParams();
    if (typeof opts?.before === 'number') p.set('before', String(opts.before));
    if (typeof opts?.after === 'number') p.set('after', String(opts.after));
    const qs = p.toString();
    return this.request<{
      threadId: string;
      peer: PublicUser | null;
      target: DmMessage;
      before: DmMessage[];
      after: DmMessage[];
      hasMoreBefore: boolean;
      hasMoreAfter: boolean;
    }>('GET', `/me/dms/${userId}/messages/${messageId}/context${qs ? `?${qs}` : ''}`);
  }
  // MEN-337 — `replyToId` optionnel : répondre/citer un message du fil.
  // MEN-342 — `attachmentIds` optionnel : pièces jointes (parité Discord),
  // ids d'Attachments créés via signUpload + PUT + finalizeUpload. Si
  // `content` est vide mais qu'il y a des pièces jointes, le message est
  // accepté (message à pièces jointes seules). Additif : les appels
  // `sendDm(id, content)` / `sendDm(id, content, replyToId)` restent valides.
  sendDm(userId: string, content: string, replyToId?: string, attachmentIds?: string[]) {
    return this.request<DmMessage>('POST', `/me/dms/${userId}/messages`, {
      content,
      ...(replyToId ? { replyToId } : {}),
      ...(attachmentIds && attachmentIds.length > 0 ? { attachmentIds } : {}),
    });
  }
  // MEN-263 (parité Discord) — éditer un message direct dont je suis l'auteur.
  editDm(userId: string, messageId: string, content: string) {
    return this.request<DmMessage>('PATCH', `/me/dms/${userId}/messages/${messageId}`, { content });
  }
  // MEN-263 (parité Discord) — supprimer (soft-delete) un de mes messages directs.
  deleteDm(userId: string, messageId: string) {
    return this.request<DmMessage>('DELETE', `/me/dms/${userId}/messages/${messageId}`);
  }
  // MEN-334 — marque la conversation avec `userId` comme lue (idempotent).
  markDmRead(userId: string) {
    return this.request<{ threadId: string; readerId: string; readAt: string }>(
      'POST',
      `/me/dms/${userId}/read`,
    );
  }
  // MEN-340 (parité Discord) — toggle une réaction emoji sur un message
  // direct : re-POST du même emoji déjà posé par moi = retrait. Renvoie
  // le message à jour (avec ses réactions agrégées).
  toggleDmReaction(userId: string, messageId: string, emoji: string) {
    return this.request<DmMessage>('POST', `/me/dms/${userId}/messages/${messageId}/reactions`, {
      emoji,
    });
  }

  // MEN-266 (parité Discord « see who reacted ») — liste des réacteurs
  // d'un message direct, groupés par emoji, profils hydratés DANS
  // L'ORDRE DE RÉACTION (le DTO message ne porte que des userIds bruts
  // triés alpha). GET method-distinct sur le path du toggle. Read-only,
  // friends-gated.
  listDmReactions(userId: string, messageId: string) {
    return this.request<{
      threadId: string;
      messageId: string;
      reactions: Array<{
        emoji: string;
        count: number;
        mine: boolean;
        userIds: string[];
        users: PublicUser[];
      }>;
    }>('GET', `/me/dms/${userId}/messages/${messageId}/reactions`);
  }

  // MEN-266 (parité Discord) — ancre du premier message non-lu d'un DM :
  // où positionner le séparateur « Nouveaux messages » / l'auto-scroll à
  // l'ouverture. `GET /me/dms` donne déjà unreadCount mais pas l'ancre.
  // Read-only, friends-gated, sans effet de bord (ne crée pas le thread).
  dmUnreadAnchor(userId: string) {
    return this.request<{
      threadId: string | null;
      anchorMessageId: string | null;
      unreadCount: number;
      hasUnread: boolean;
    }>('GET', `/me/dms/${userId}/unread-anchor`);
  }

  // MEN-266 (parité Discord) — galerie « Médias / Fichiers partagés »
  // d'un DM : messages porteurs d'une pièce jointe, du plus récent au
  // plus ancien, filtrable kind (all|image|file), pagination keyset
  // (nextCursor opaque à repasser en `cursor`, null = fin). Read-only,
  // friends-gated, sans effet de bord (ne crée pas le thread).
  dmMedia(
    userId: string,
    opts?: { kind?: 'all' | 'image' | 'file'; limit?: number; cursor?: string },
  ) {
    const p = new URLSearchParams();
    if (opts?.kind) p.set('kind', opts.kind);
    if (opts?.limit) p.set('limit', String(opts.limit));
    if (opts?.cursor) p.set('cursor', opts.cursor);
    const qs = p.toString();
    return this.request<{
      threadId: string | null;
      kind: 'all' | 'image' | 'file';
      items: {
        messageId: string;
        authorId: string;
        createdAt: string;
        attachments: {
          id: string;
          filename: string;
          mimeType: string;
          sizeBytes: number;
          url: string;
          isImage: boolean;
        }[];
      }[];
      hasMore: boolean;
      nextCursor: string | null;
    }>('GET', `/me/dms/${userId}/media${qs ? `?${qs}` : ''}`);
  }

  // MEN-266 — verdict « puis-je écrire à cet utilisateur ? » : permet à un
  // client (desktop/admin) de désactiver/activer le composer DM AVANT toute
  // tentative d'envoi, sans deviner les règles serveur (amitié acceptée).
  canSendDm(userId: string) {
    return this.request<{
      canSend: boolean;
      reason: DmSendReason;
      target: PublicUser;
      /** [2.11.0] Présent quand une demande de message existe entre nous. */
      request?: DmThreadRequestState;
    }>('GET', `/me/dms/${userId}/can-send`);
  }

  // ----- [2.11.0] Demandes de message -----
  // Serveur de GUILDE : attestation « nous partageons ce serveur » pour le
  // membre `userId` (id LOCAL à la guilde), à remettre au serveur de comptes.
  createDmAttestation(userId: string) {
    return this.request<DmAttestationGrant>('POST', '/me/dm-attestations', { userId });
  }
  // Serveur de COMPTES (ou serveur à comptes locaux) : envoie une demande de
  // message avec son premier message. 201 : demande et message créés.
  sendDmRequest(input: SendDmRequestInput) {
    return this.request<DmRequestCreated>('POST', '/me/dm-requests', input);
  }
  // Demandes en attente : reçues (défaut) ou envoyées.
  listDmRequests(direction: DmRequestDirection = 'incoming') {
    return this.request<{ requests: DmRequest[] }>(
      'GET',
      `/me/dm-requests?direction=${encodeURIComponent(direction)}`,
    );
  }
  // Nombre de demandes reçues en attente (badge « Demandes de message »).
  dmRequestCount() {
    return this.request<{ incoming: number }>('GET', '/me/dm-requests/count');
  }
  acceptDmRequest(requestId: string) {
    return this.request<{ request: DmRequest }>(
      'POST',
      `/me/dm-requests/${encodeURIComponent(requestId)}/accept`,
    );
  }
  refuseDmRequest(requestId: string) {
    return this.request<{ request: DmRequest }>(
      'POST',
      `/me/dm-requests/${encodeURIComponent(requestId)}/refuse`,
    );
  }
  // Serveur de COMPTES : clé d'un serveur de guilde que je viens de rejoindre.
  registerLinkedServerKey(input: RegisterLinkedServerKeyInput) {
    return this.request<{ ok: true; changed: boolean }>('POST', '/me/linked-server-keys', input);
  }
  listLinkedServerKeys() {
    return this.request<{ keys: LinkedServerKeyView[] }>('GET', '/me/linked-server-keys');
  }
  // Oublie la clé de ce serveur et annule les demandes reçues en attente passées par lui.
  removeLinkedServerKey(audience: string) {
    return this.request<{ removed: number; cancelledRequests: number }>(
      'DELETE',
      `/me/linked-server-keys?audience=${encodeURIComponent(audience)}`,
    );
  }

  // [2.9] Appels en conversation privée (1:1, amis). Jeton LiveKit de la
  // salle de la conversation (micro, caméra, partage d'écran et son du partage).
  dmCallToken(userId: string) {
    return this.request<DmCallGrant>('POST', `/me/dms/${userId}/call/token`);
  }

  // [2.9] Appel en cours dans la conversation avec `userId` (null : aucun).
  getDmCall(userId: string) {
    return this.request<{ call: DmCallState | null }>('GET', `/me/dms/${userId}/call`);
  }

  // [2.9] Appels en cours qui me concernent (dont ceux qui me font sonner).
  listDmCalls() {
    return this.request<{ calls: DmCallState[] }>('GET', '/me/dm-calls');
  }

  // MEN-266 — badge global de DM non-lus : agrégat léger (aucune
  // hydratation des pairs ni des derniers messages) pour la pastille
  // de l'app / l'onglet « Messages ». Contrepartie « compteur » de la
  // liste hydratée `getDms()`.
  dmUnreadCount() {
    return this.request<{
      total: number;
      unreadThreadCount: number;
      threads: { threadId: string; peerId: string; unreadCount: number }[];
    }>('GET', '/me/dm-unread-count');
  }

  // MEN-266 — statistiques d'une conversation DM (panneau « infos de
  // conversation », parité Discord/WhatsApp). Vue dérivée agrégée du fil
  // (totaux par auteur, édités/supprimés, pièces jointes, bornes
  // temporelles), contrepartie « résumé » de getDmMessages().
  dmConversationStats(peerId: string) {
    return this.request<{
      threadId: string;
      peer: PublicUser | null;
      stats: {
        totalMessages: number;
        myMessages: number;
        peerMessages: number;
        editedMessages: number;
        deletedMessages: number;
        withAttachments: number;
        attachmentsTotal: number;
        firstMessageAt: string | null;
        lastMessageAt: string | null;
      };
    }>('GET', `/me/dms/${encodeURIComponent(peerId)}/stats`);
  }

  // MEN-266 — série temporelle d'activité d'une conversation DM
  // (sparkline du panneau « infos de conversation »). Contrepartie
  // « série » de dmConversationStats() (agrégats ponctuels) : un
  // bucket par jour calendaire UTC sur `days` jours (1..90, défaut 30 ;
  // clampé serveur), zéros remplis, du plus ancien au plus récent.
  dmActivity(peerId: string, days?: number) {
    const qs =
      typeof days === 'number' && Number.isFinite(days)
        ? `?days=${encodeURIComponent(String(Math.trunc(days)))}`
        : '';
    return this.request<{
      threadId: string;
      peer: PublicUser | null;
      activity: {
        days: number;
        from: string;
        to: string;
        buckets: { date: string; count: number }[];
        total: number;
      };
    }>('GET', `/me/dms/${encodeURIComponent(peerId)}/activity${qs}`);
  }

  // MEN-266 — latences de réponse d'une conversation DM (« temps de
  // réponse moyen »). Dimension distincte de dmConversationStats
  // (agrégats ponctuels) et dmActivity (série par jour) : délai entre
  // tours de parole consécutifs, agrégé par direction (mes réponses /
  // ses réponses / global). `maxGapMs` (60 000..2 592 000 000, défaut
  // 86 400 000 ; clampé serveur) écarte les reprises après silence.
  // Chaque agrégat est `null` quand aucune réponse n'est comptée.
  dmResponseTimes(peerId: string, opts?: { maxGapMs?: number }) {
    const g = opts?.maxGapMs;
    const qs =
      typeof g === 'number' && Number.isFinite(g)
        ? `?maxGapMs=${encodeURIComponent(String(Math.trunc(g)))}`
        : '';
    type Stat = {
      count: number;
      avgMs: number;
      medianMs: number;
      p90Ms: number;
      minMs: number;
      maxMs: number;
    };
    return this.request<{
      threadId: string;
      peer: PublicUser | null;
      responseTimes: {
        maxGapMs: number;
        mine: Stat | null;
        peer: Stat | null;
        overall: Stat | null;
      };
    }>('GET', `/me/dms/${encodeURIComponent(peerId)}/response-times${qs}`);
  }

  // MEN-266 — série de jours consécutifs actifs d'une conversation DM
  // (« streak », engagement façon Discord/Snapchat). Dimension
  // distincte de dmActivity (counts par jour) : plus longue suite de
  // jours calendaires consécutifs avec au moins un message + série en
  // cours (grâce J/J-1) + total de jours actifs, dans le fuseau du
  // client. `tzOffsetMinutes` (-840..840, défaut 0 ; clampé serveur)
  // et `todayMs` (horloge ; défaut serveur). Tout 0 / null si aucun
  // message échangé.
  dmStreak(peerId: string, opts?: { tzOffsetMinutes?: number; todayMs?: number }) {
    const parts: string[] = [];
    const tz = opts?.tzOffsetMinutes;
    if (typeof tz === 'number' && Number.isFinite(tz)) {
      parts.push(`tzOffsetMinutes=${encodeURIComponent(String(Math.trunc(tz)))}`);
    }
    const t = opts?.todayMs;
    if (typeof t === 'number' && Number.isFinite(t)) {
      parts.push(`todayMs=${encodeURIComponent(String(Math.trunc(t)))}`);
    }
    const qs = parts.length ? `?${parts.join('&')}` : '';
    return this.request<{
      threadId: string;
      peer: PublicUser | null;
      streak: {
        tzOffsetMinutes: number;
        currentStreakDays: number;
        longestStreakDays: number;
        lastActiveDay: string | null;
        firstActiveDay: string | null;
        totalActiveDays: number;
      };
    }>('GET', `/me/dms/${encodeURIComponent(peerId)}/streak${qs}`);
  }

  // MEN-266 (parité Snapchat/FB Memories) — « on this day » :
  // récap des messages tombant le même jour calendaire (MM-DD)
  // lors des années précédentes. `monthDay` 'MM-DD' (défaut
  // serveur = jour local du jour), `tzOffsetMinutes` (défaut 0),
  // `limitPerYear` (défaut 5, clampé [1, 50]).
  dmOnThisDay(
    peerId: string,
    opts?: { monthDay?: string; tzOffsetMinutes?: number; limitPerYear?: number },
  ) {
    const parts: string[] = [];
    const md = opts?.monthDay;
    if (typeof md === 'string' && md.length > 0) {
      parts.push(`monthDay=${encodeURIComponent(md)}`);
    }
    const tz = opts?.tzOffsetMinutes;
    if (typeof tz === 'number' && Number.isFinite(tz)) {
      parts.push(`tzOffsetMinutes=${encodeURIComponent(String(Math.trunc(tz)))}`);
    }
    const lim = opts?.limitPerYear;
    if (typeof lim === 'number' && Number.isFinite(lim)) {
      parts.push(`limitPerYear=${encodeURIComponent(String(Math.trunc(lim)))}`);
    }
    const qs = parts.length ? `?${parts.join('&')}` : '';
    return this.request<{
      threadId: string;
      peer: PublicUser | null;
      onThisDay: {
        tzOffsetMinutes: number;
        monthDay: string;
        limitPerYear: number;
        totalMessages: number;
        years: Array<{
          year: number;
          count: number;
          firstAt: string;
          lastAt: string;
          messages: Array<{
            id: string;
            authorId: string;
            createdAt: string;
            content: string;
          }>;
        }>;
      };
    }>('GET', `/me/dms/${encodeURIComponent(peerId)}/on-this-day${qs}`);
  }

  // MEN-266 (parité Discord punchcard) — heatmap rythme
  // hebdomadaire : grille 7×24 (jour de semaine 0=lundi × heure
  // locale) du volume de messages sur une fenêtre glissante de
  // `windowWeeks` semaines (défaut 12, clampé [1, 260]). Dimension
  // distincte de dmActivity (counts par jour) et dmStreak (jours
  // consécutifs). `tzOffsetMinutes` (-840..840, défaut 0 ; clampé
  // serveur) et `todayMs` (horloge ; défaut serveur).
  dmHeatmap(
    peerId: string,
    opts?: { tzOffsetMinutes?: number; windowWeeks?: number; todayMs?: number },
  ) {
    const parts: string[] = [];
    const tz = opts?.tzOffsetMinutes;
    if (typeof tz === 'number' && Number.isFinite(tz)) {
      parts.push(`tzOffsetMinutes=${encodeURIComponent(String(Math.trunc(tz)))}`);
    }
    const w = opts?.windowWeeks;
    if (typeof w === 'number' && Number.isFinite(w)) {
      parts.push(`windowWeeks=${encodeURIComponent(String(Math.trunc(w)))}`);
    }
    const t = opts?.todayMs;
    if (typeof t === 'number' && Number.isFinite(t)) {
      parts.push(`todayMs=${encodeURIComponent(String(Math.trunc(t)))}`);
    }
    const qs = parts.length ? `?${parts.join('&')}` : '';
    return this.request<{
      threadId: string;
      peer: PublicUser | null;
      heatmap: {
        tzOffsetMinutes: number;
        windowWeeks: number;
        since: string;
        until: string;
        totalMessages: number;
        grid: number[][];
        byWeekday: number[];
        byHour: number[];
        peak: { weekday: number; hour: number; count: number } | null;
      };
    }>('GET', `/me/dms/${encodeURIComponent(peerId)}/heatmap${qs}`);
  }

  // MEN-266 §30 (jalons de relation) — pour chaque palier de volume
  // du fil (1,2,5,…,10000), s'il est atteint avec date + auteur du
  // Nème message, plus le prochain palier et les messages restants.
  // Dimension distincte de dmStats (totaux), dmStreak (jours
  // consécutifs), dmActivity (série par jour) et dmHeatmap (grille
  // 7×24). Aucun paramètre.
  dmMilestones(peerId: string) {
    return this.request<{
      threadId: string;
      peer: PublicUser | null;
      milestones: {
        total: number;
        firstMessageAt: string | null;
        lastMessageAt: string | null;
        milestones: {
          threshold: number;
          reached: boolean;
          index: number | null;
          messageId: string | null;
          at: string | null;
          authorId: string | null;
        }[];
        nextMilestone: { threshold: number; remaining: number } | null;
      };
    }>('GET', `/me/dms/${encodeURIComponent(peerId)}/milestones`);
  }

  // Équilibre / réciprocité du fil DM (§31) : byAuthor (count +
  // share) trié décroissant, initiateur, dernier auteur, auteur
  // dominant (null si égalité), monologue le plus long, ratio de
  // réciprocité min/max. Dimension distincte de dmStats (totaux),
  // dmStreak, dmActivity, dmHeatmap et dmMilestones. Aucun paramètre.
  dmBalance(peerId: string) {
    return this.request<{
      threadId: string;
      peer: PublicUser | null;
      balance: {
        total: number;
        byAuthor: { authorId: string; count: number; share: number }[];
        initiatorId: string | null;
        lastAuthorId: string | null;
        dominantAuthorId: string | null;
        longestStreak: { authorId: string; length: number } | null;
        reciprocityRatio: number | null;
      };
    }>('GET', `/me/dms/${encodeURIComponent(peerId)}/balance`);
  }

  // Silences du fil DM (§32) : nombre d'intervalles, moyenne et médiane
  // des gaps inter-messages, plus longs gaps (longest + topGaps) et
  // durée du silence courant (depuis le dernier message jusqu'à
  // maintenant). DISTINCT de dmStats/dmStreak/dmActivity/dmHeatmap/
  // dmMilestones/dmBalance et dmResponseTimes (latence RÉCIPROQUE entre
  // auteurs différents). Ici on mesure le TEMPS ABSOLU entre messages
  // consécutifs, peu importe les auteurs. Aucun paramètre.
  dmLulls(peerId: string) {
    return this.request<{
      threadId: string;
      peer: PublicUser | null;
      lulls: {
        total: number;
        intervals: number;
        averageGapSeconds: number | null;
        medianGapSeconds: number | null;
        longest: {
          fromMessageId: string;
          toMessageId: string;
          fromAt: string;
          toAt: string;
          seconds: number;
        } | null;
        topGaps: {
          fromMessageId: string;
          toMessageId: string;
          fromAt: string;
          toAt: string;
          seconds: number;
        }[];
        currentLullSeconds: number | null;
      };
    }>('GET', `/me/dms/${encodeURIComponent(peerId)}/lulls`);
  }

  // Distribution des longueurs de messages d'un fil DM (§33) :
  // totalChars, averageChars, medianChars, longest/shortest (avec
  // messageId/authorId/chars/createdAt) et byAuthor (count +
  // totalChars + averageChars + shareOfChars). DISTINCT de dmStats
  // (totaux msg sans chars), dmBalance (qui parle le plus en count),
  // dmResponseTimes (latence), dmHeatmap/dmActivity (temps),
  // dmMilestones (paliers volume) et dmLulls (silences). Ici on
  // analyse le CONTENU CARACTÈRES ('string.length' = code units
  // UTF-16, cohérent avec la colonne `content` PG). Utile pour
  // "qui écrit les pavés" et "longueur moyenne de nos échanges".
  // Aucun paramètre.
  dmLengths(peerId: string) {
    return this.request<{
      threadId: string;
      peer: PublicUser | null;
      lengths: {
        total: number;
        totalChars: number;
        averageChars: number | null;
        medianChars: number | null;
        longest: {
          messageId: string;
          authorId: string;
          chars: number;
          createdAt: string;
        } | null;
        shortest: {
          messageId: string;
          authorId: string;
          chars: number;
          createdAt: string;
        } | null;
        byAuthor: {
          authorId: string;
          count: number;
          totalChars: number;
          averageChars: number;
          shareOfChars: number;
        }[];
      };
    }>('GET', `/me/dms/${encodeURIComponent(peerId)}/lengths`);
  }

  // Distribution d'usage des emoji dans un fil DM (§35) :
  // totalEmojis, messagesWithEmoji, byAuthor (count msg avec emoji +
  // totalEmojis + emojiPerMessage + shareOfEmojis), topEmojis (top 10
  // count + share), mostEmojiMessage (1ʳᵉ occurrence à count max).
  // DISTINCT de dmStats (totaux), dmBalance (count par auteur),
  // dmResponseTimes (latence), dmHeatmap/dmActivity (temps),
  // dmMilestones (paliers volume), dmLulls (silences) et dmLengths
  // (chars). Ici on parse les emoji via `\p{Extended_Pictographic}`
  // (Unicode standard ES2018+). Utile pour "qui est le plus
  // emoji-ique" et "emoji favori du fil". Aucun paramètre.
  dmEmojis(peerId: string) {
    return this.request<{
      threadId: string;
      peer: PublicUser | null;
      emojis: {
        total: number;
        totalEmojis: number;
        messagesWithEmoji: number;
        byAuthor: {
          authorId: string;
          count: number;
          totalEmojis: number;
          emojiPerMessage: number;
          shareOfEmojis: number;
        }[];
        topEmojis: { emoji: string; count: number; share: number }[];
        mostEmojiMessage: {
          messageId: string;
          authorId: string;
          count: number;
          createdAt: string;
        } | null;
      };
    }>('GET', `/me/dms/${encodeURIComponent(peerId)}/emojis`);
  }

  // Marqueurs de ton (?, !) dans un fil DM (§36) : `byAuthor` avec
  // questions (chars `?`), exclamations (chars `!`),
  // messagesWithQuestion/Exclamation, questionRatio/exclamationRatio.
  // DISTINCT de dmStats, dmBalance, dmEmojis (Pictographic ≠
  // punctuation), dmLengths (chars total). Utile pour "qui pose
  // des questions" et "qui crie". Aucun paramètre.
  dmTone(peerId: string) {
    return this.request<{
      threadId: string;
      peer: PublicUser | null;
      tone: {
        total: number;
        totalQuestionMarks: number;
        totalExclamationMarks: number;
        messagesWithQuestion: number;
        messagesWithExclamation: number;
        byAuthor: {
          authorId: string;
          totalMessages: number;
          questions: number;
          exclamations: number;
          messagesWithQuestion: number;
          messagesWithExclamation: number;
          questionRatio: number;
          exclamationRatio: number;
        }[];
      };
    }>('GET', `/me/dms/${encodeURIComponent(peerId)}/tone`);
  }

  // Partage de liens dans un fil DM (§37) : totalLinks,
  // messagesWithLink, byAuthor (count msg avec lien + totalLinks +
  // linksPerMessage + shareOfLinks), top 10 domaines normalisés
  // (lowercase, sans `www.`, sans port), mostLinkedMessage. DISTINCT
  // de dmStats, dmBalance, dmEmojis (Pictographic ≠ URL), dmTone
  // (punct ≠ URL), dmLengths (chars total). Utile pour "retrouver
  // un lien partagé" et "qui partage le plus de liens". Aucun
  // paramètre.
  dmLinks(peerId: string) {
    return this.request<{
      threadId: string;
      peer: PublicUser | null;
      links: {
        total: number;
        totalLinks: number;
        messagesWithLink: number;
        byAuthor: {
          authorId: string;
          count: number;
          totalLinks: number;
          linksPerMessage: number;
          shareOfLinks: number;
        }[];
        topDomains: { domain: string; count: number; share: number }[];
        mostLinkedMessage: {
          messageId: string;
          authorId: string;
          count: number;
          createdAt: string;
        } | null;
      };
    }>('GET', `/me/dms/${encodeURIComponent(peerId)}/links`);
  }

  // Intensité MAJUSCULE / criage (§38) : `byAuthor` avec letters,
  // uppercase, uppercaseRatio, shoutyMessages (ratio ≥0.7 ET
  // letters ≥5), shoutiestMessage (msg au ratio max). DISTINCT de
  // dmTone (`?!`), dmEmojis (Pictographic), dmLinks (URL),
  // dmLengths (chars total). Utile pour "qui crie le plus" et
  // "message le plus en majuscules". Aucun paramètre.
  dmCaps(peerId: string) {
    return this.request<{
      threadId: string;
      peer: PublicUser | null;
      caps: {
        total: number;
        totalLetters: number;
        totalUppercase: number;
        byAuthor: {
          authorId: string;
          totalMessages: number;
          letters: number;
          uppercase: number;
          uppercaseRatio: number;
          shoutyMessages: number;
        }[];
        shoutiestMessage: {
          messageId: string;
          authorId: string;
          letters: number;
          uppercase: number;
          uppercaseRatio: number;
          createdAt: string;
        } | null;
      };
    }>('GET', `/me/dms/${encodeURIComponent(peerId)}/caps`);
  }

  // ----- Admin -----
  // [2.13.0] `opts.cursor` = id du dernier compte reçu (page suivante), `limit` ≤ 200.
  adminListUsers(q?: string, opts: { cursor?: string; limit?: number } = {}) {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (opts.cursor) p.set('cursor', opts.cursor);
    if (opts.limit !== undefined) p.set('limit', String(opts.limit));
    const qs = p.toString();
    return this.request<User[]>('GET', `/admin/users${qs ? `?${qs}` : ''}`);
  }
  adminDisableUser(id: string) {
    return this.request<{ ok: true }>('POST', `/admin/users/${id}/disable`);
  }
  adminEnableUser(id: string) {
    return this.request<{ ok: true }>('POST', `/admin/users/${id}/enable`);
  }
  // [2.13.0] Serveurs Siverix connus du serveur de comptes (404 ailleurs).
  adminListSiverixServers() {
    return this.request<{ servers: SiverixServerSummary[] }>('GET', '/admin/siverix-servers');
  }
  adminBlockSiverixServer(url: string, reason?: string) {
    return this.request<{
      url: string;
      blocked: true;
      blockedReason: string | null;
      blockedAt: string;
    }>('POST', '/admin/siverix-servers/block', reason ? { url, reason } : { url });
  }
  adminUnblockSiverixServer(url: string) {
    return this.request<{ url: string; blocked: false }>('DELETE', '/admin/siverix-servers/block', {
      url,
    });
  }
  adminSiverixServerStatus(url: string) {
    return this.request<SiverixServerStatus>(
      'GET',
      `/admin/siverix-servers/status?url=${encodeURIComponent(url)}`,
    );
  }
  adminListCommunities() {
    return this.request<Array<Community & { _count?: { members: number; channels: number } }>>(
      'GET',
      '/admin/communities',
    );
  }
  adminAuditLog(params?: AuditLogQuery) {
    return this.request<AuditLog[]>('GET', `/admin/audit-log${auditLogQs(params)}`);
  }
  /**
   * Export CSV du journal d'audit (vue filtrée courante). Renvoie le
   * texte CSV brut (text/csv). Fetch direct authentifié car `request`
   * parse en JSON.
   */
  async adminAuditLogCsv(params?: AuditLogQuery): Promise<string> {
    const headers: Record<string, string> = {};
    const token = this.getToken();
    if (token) headers.authorization = `Bearer ${token}`;
    const res = await this.fetchImpl(
      `${this.baseUrl}/admin/audit-log${auditLogQs({ ...params, format: 'csv' })}`,
      { method: 'GET', headers, credentials: 'include' },
    );
    if (res.status === 401) this.onUnauthorized?.();
    if (!res.ok) {
      throw new ApiError(res.status, 'HttpError', res.statusText);
    }
    return res.text();
  }
  adminInstance() {
    return this.request<InstanceConfig>('GET', '/admin/instance');
  }
  adminUpdateInstance(data: Partial<InstanceConfig>) {
    return this.request<InstanceConfig>('PATCH', '/admin/instance', data);
  }

  // ----- MEN-239 : modération / bans / invitations instance -----
  adminListReports(params?: { status?: string; limit?: number; cursor?: string }) {
    const sp = new URLSearchParams();
    if (params?.status) sp.set('status', params.status);
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.cursor) sp.set('cursor', params.cursor);
    const qs = sp.toString();
    return this.request<AdminReport[]>('GET', `/admin/reports${qs ? `?${qs}` : ''}`);
  }
  adminUpdateReport(id: string, status: AdminReport['status']) {
    return this.request<AdminReport>('PATCH', `/admin/reports/${id}`, { status });
  }
  adminListBans() {
    return this.request<AdminBan[]>('GET', '/admin/bans');
  }
  adminCreateBan(data: { userId: string; reason: string; expiresAt?: string }) {
    return this.request<AdminBan>('POST', '/admin/bans', data);
  }
  adminDeleteBan(userId: string) {
    return this.request<{ ok: true }>('DELETE', `/admin/bans/${userId}`);
  }
  adminListInvites() {
    return this.request<AdminInvite[]>('GET', '/admin/invites');
  }
  adminCreateInvite(data?: { maxUses?: number; expiresAt?: string }) {
    return this.request<AdminInvite>('POST', '/admin/invites', data ?? {});
  }
  adminDeleteInvite(code: string) {
    return this.request<{ ok: true }>('DELETE', `/admin/invites/${code}`);
  }

  // ----- MEN-237 : rôles community (surface admin) -----
  adminListRoles(communityId: string) {
    return this.request<AdminRole[]>('GET', `/admin/communities/${communityId}/roles`);
  }
  adminCreateRole(
    communityId: string,
    data: {
      name: string;
      color?: number;
      permissions?: string;
      position?: number;
      hoist?: boolean;
      mentionable?: boolean;
    },
  ) {
    return this.request<AdminRole>('POST', `/admin/communities/${communityId}/roles`, data);
  }
  adminUpdateRole(
    communityId: string,
    roleId: string,
    data: {
      name?: string;
      color?: number;
      permissions?: string;
      position?: number;
      hoist?: boolean;
      mentionable?: boolean;
    },
  ) {
    return this.request<AdminRole>(
      'PATCH',
      `/admin/communities/${communityId}/roles/${roleId}`,
      data,
    );
  }
  adminDeleteRole(communityId: string, roleId: string) {
    return this.request<void>('DELETE', `/admin/communities/${communityId}/roles/${roleId}`);
  }

  // ----- MEN-259 : membres + ACL salon (surface admin) -----
  adminListMembers(communityId: string) {
    return this.request<AdminMember[]>('GET', `/admin/communities/${communityId}/members`);
  }
  adminAssignRole(communityId: string, userId: string, roleId: string) {
    return this.request<{ assigned: true }>(
      'PUT',
      `/admin/communities/${communityId}/members/${userId}/roles/${roleId}`,
    );
  }
  adminRevokeRole(communityId: string, userId: string, roleId: string) {
    return this.request<{ removed: true }>(
      'DELETE',
      `/admin/communities/${communityId}/members/${userId}/roles/${roleId}`,
    );
  }
  adminListChannels(communityId: string) {
    return this.request<AdminCommunityChannel[]>(
      'GET',
      `/admin/communities/${communityId}/channels`,
    );
  }
  adminListChannelPerms(channelId: string) {
    return this.request<AdminChannelOverride[]>('GET', `/admin/channels/${channelId}/permissions`);
  }
  adminPutChannelPerm(
    channelId: string,
    data: { roleId?: string; memberId?: string; allow: string; deny: string },
  ) {
    return this.request<AdminChannelOverride>(
      'PUT',
      `/admin/channels/${channelId}/permissions`,
      data,
    );
  }
  adminDeleteChannelPerm(channelId: string, owId: string) {
    return this.request<void>('DELETE', `/admin/channels/${channelId}/permissions/${owId}`);
  }
  adminEffectivePerms(channelId: string, userId: string) {
    return this.request<{ channelId: string; userId: string; permissions: string }>(
      'GET',
      `/admin/channels/${channelId}/permissions/effective?userId=${encodeURIComponent(userId)}`,
    );
  }
}
